import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { FEATURES } from './data';
import { MOBILE_BREAKPOINT } from './constants';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    backgroundColor: tokens.colorNeutralBackground2,
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  head: {
    maxWidth: '760px',
    marginInline: 'auto',
    textAlign: 'center',
    marginBlockEnd: '56px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
  },
  title: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    maxWidth: '660px',
    marginInline: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 960px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingBlock: tokens.spacingVerticalXL,
    paddingInline: tokens.spacingHorizontalXL,
    backgroundColor: ottoTokens.landingCardBackground,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'transform, border-color, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      transform: 'translateY(-2px)',
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: tokens.shadow16,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': { transform: 'none' },
    },
  },
  iconBox: {
    width: '44px',
    height: '44px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: ottoTokens.landingAccentTextCool,
    fontSize: '22px',
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1.25,
    color: tokens.colorNeutralForeground1,
  },
  desc: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
  },
});

export const FeaturesGrid = () => {
  const styles = useStyles();

  return (
    <section id="use-cases" className={styles.section} aria-labelledby="features-title">
      <div className={styles.inner}>
        <div className={styles.head}>
          <Text className={styles.eyebrow}>AI-native by design</Text>
          <Text as="h2" id="features-title" className={styles.title}>
            Built for AI from day one, not retrofitted
          </Text>
          <Text className={styles.lead}>
            MCP, RAG, and agentic orchestration are the foundation, not add-ons. Logic Apps
            Automation is designed from a clean slate and built around how modern AI workflows
            really work.
          </Text>
        </div>
        <div className={styles.grid}>
          {FEATURES.map((f) => (
            <div key={f.title} className={styles.card}>
              <div className={styles.iconBox}>
                <f.Icon />
              </div>
              <Text as="h3" className={styles.cardTitle}>
                {f.title}
              </Text>
              <Text className={styles.desc}>{f.desc}</Text>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
